import type { Field } from '@/api/types/crm/crm.types'
import type { CrmLeadParent } from '@/api/types/crm/crmLeadParent.types'
import type { LeadProfileLead } from '../../../LeadProfileModal.types'

export type LeadIntegrationContextTab =
  | { type: 'lead' }
  | { type: 'parent'; parentId: number }

/** Dirty values родителя: parentId → fieldId → raw input value. */
export type ParentIntegrationDrafts = Record<
  number,
  Record<string, string>
>

export const getCrmLeadParents = (
  lead: LeadProfileLead | undefined,
): CrmLeadParent[] => {
  const parents = lead?.parents
  const list = Array.isArray(parents) ? parents : []
  return list.filter(
    (item) => item?.id != null && Number.isFinite(Number(item.id)),
  )
}

export const getLeadIntegrationTabLabel = (): string => 'Лид'

export const getParentIntegrationTabLabel = (parent: CrmLeadParent): string => {
  const name = String(parent.name ?? '').trim()
  if (name) return name
  const phone = String(parent.phone ?? '').trim()
  if (phone) return `Родитель · ${phone}`
  return 'Родитель'
}

const readParentFieldValue = (
  parent: CrmLeadParent | undefined,
  fieldId: number,
): string => {
  if (!parent?.integration_field_values) return ''
  const map = parent.integration_field_values
  const entry = map[String(fieldId)] ?? map[fieldId as unknown as string]
  const value = entry?.value
  return value == null ? '' : String(value)
}

/** fieldsData для сайдбара на вкладке родителя (ключ = unique_name). */
export const buildParentIntegrationFieldsData = (
  fields: Field[],
  parent: CrmLeadParent | undefined,
  draft: Record<string, string> | undefined,
): Record<string, string> => {
  const out: Record<string, string> = {}
  for (const field of fields) {
    const idKey = String(field.id)
    if (draft && Object.prototype.hasOwnProperty.call(draft, idKey)) {
      out[field.unique_name] = draft[idKey]
      continue
    }
    out[field.unique_name] = readParentFieldValue(parent, Number(field.id))
  }
  return out
}

/**
 * Источник чатов для `findLeadChatForIntegrationField`.
 * На вкладке родителя — только `parent.integrations` (не lead.integrations).
 */
export const buildChatSourceLead = (
  lead: LeadProfileLead,
  parent: CrmLeadParent | null | undefined,
): LeadProfileLead => {
  if (!parent) {
    return {
      ...lead,
      integrations: {},
    } as LeadProfileLead
  }
  return {
    ...lead,
    integrations: parent.integrations ?? {},
  } as LeadProfileLead
}

/** Подмена элемента `parents[]` после PUT integration-fields. */
export const replaceCrmLeadParentInList = (
  parents: CrmLeadParent[] | undefined,
  nextParent: CrmLeadParent,
): CrmLeadParent[] => {
  const list = Array.isArray(parents) ? parents : []
  let replaced = false
  const next = list.map((item) => {
    if (Number(item.id) !== Number(nextParent.id)) return item
    replaced = true
    return nextParent
  })
  return replaced ? next : [...next, nextParent]
}

export const resolveActiveIntegrationTab = (
  tab: LeadIntegrationContextTab,
  parents: CrmLeadParent[],
): LeadIntegrationContextTab => {
  if (tab.type === 'lead') return tab
  if (parents.some((item) => Number(item.id) === Number(tab.parentId))) {
    return tab
  }
  return { type: 'lead' }
}
